import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { i18n as I18nInstance } from 'i18next';
import { ensureNamespaces, hasNamespaces } from '@/i18n/config';
import { namespacesForPath } from '@/i18n/namespaces';

type Load = { promise: Promise<void>; settled: boolean };

const loads = new WeakMap<I18nInstance, Map<string, Load>>();

/** The load to wait for before the route can render, or null when there is none. */
function pendingLoad(i18n: I18nInstance, language: string, namespaces: readonly string[]): Promise<void> | null {
  if (hasNamespaces(i18n, language, namespaces)) return null;

  let byKey = loads.get(i18n);
  if (!byKey) {
    byKey = new Map();
    loads.set(i18n, byKey);
  }
  const key = `${language}:${namespaces.join(',')}`;
  const existing = byKey.get(key);
  // A load that settled without the bundles failed: render the page rather
  // than suspend it forever.
  if (existing) return existing.settled ? null : existing.promise;

  const load: Load = { settled: false, promise: Promise.resolve() };
  load.promise = ensureNamespaces(i18n, language, namespaces)
    .catch(() => undefined)
    .finally(() => {
      load.settled = true;
    });
  byKey.set(key, load);
  return load.promise;
}

/**
 * Holds a route until its i18n namespaces (e.g. a trade page's copy) are loaded,
 * so it never renders raw keys like `plumber.hero.title`. Navigations run in a
 * transition, so React keeps the previous page on screen while it waits.
 */
const I18nRouteNamespaces = ({ children }: { children: ReactNode }) => {
  const { pathname } = useLocation();
  const { i18n } = useTranslation();

  const load = pendingLoad(i18n, i18n.language, namespacesForPath(pathname));
  if (load) throw load;

  return <>{children}</>;
};

export default I18nRouteNamespaces;
